/**
 * The "Deadlines pushed out" report's wire shape, mirrored from
 * services/client-functions/src/pipelineTracker/reports/deadlineShift.ts.
 *
 * A pure module with NO imports, for the reason reportContract.ts gives: anything importing a hook
 * drags lib/firebase into the test runner. Declared here, not imported, because the browser bundle
 * cannot reach the functions package and the tracker isolation fence forbids a shared one.
 *
 * This string is a Zod literal on the server. A mismatch is a runtime "invalid-argument", not a
 * type error on either side.
 */

export const DEADLINE_SHIFT_REPORT_ACTION = 'deadlineShiftReport';

export interface DeadlineShiftWindow {
  firstAsAtDate: string;
  asAtDate: string;
  snapshotCount: number;
  snapshotsAvailable: number;
  pairsCompared: number;
}

export interface DeadlineShiftBuilding {
  status: 'building';
  snapshotCount: number;
  snapshotsAvailable: number;
  snapshotsNeeded: number;
  firstAsAtDate: string | null;
  asAtDate: string | null;
}

export interface BrokerPushSummary {
  adviserKey: string;
  adviserName: string;
  dealsWithPush: number;
  pushes: number;
  medianDaysPushed: number | null;
  repeatDeals: number;
}

export interface AssigneePushSummary {
  assigneeName: string;
  tasksWithPush: number;
  pushes: number;
  medianDaysPushed: number | null;
  repeatTasks: number;
}

export interface PushedDeal {
  dealRef: string;
  adviserName: string;
  pushes: number;
  totalDaysPushed: number;
  firstEstimate: string;
  latestEstimate: string | null;
  stage: string | null;
}

export interface PushedTask {
  /** The mapped task group, never the raw task name. */
  taskName: string;
  dealRef: string | null;
  assigneeName: string;
  pushes: number;
  totalDaysPushed: number;
}

export interface DealPushPart {
  status: 'ok';
  window: DeadlineShiftWindow;
  totals: { dealsWithPush: number; pushes: number; medianDaysPushed: number | null; repeatDeals: number };
  brokers: BrokerPushSummary[];
  topDeals: PushedDeal[];
}

export interface TaskPushPart {
  status: 'ok';
  window: DeadlineShiftWindow;
  totals: { tasksWithPush: number; pushes: number; medianDaysPushed: number | null; repeatTasks: number };
  assignees: AssigneePushSummary[];
  topTasks: PushedTask[];
}

export interface DeadlineShiftResult {
  status: 'ok';
  minPushDays: number;
  snapshotCap: number;
  adviserFilter: { adviserKey: string; adviserName: string | null } | null;
  deals: DealPushPart | DeadlineShiftBuilding;
  tasks: TaskPushPart | DeadlineShiftBuilding;
}

/** "latest 26 of 30 uploads" when history was capped, else "4 uploads". */
export function uploadsLabel(window: DeadlineShiftWindow): string {
  const n = window.snapshotCount;
  if (window.snapshotsAvailable > n) return `latest ${n} of ${window.snapshotsAvailable} uploads`;
  return `${n} upload${n === 1 ? '' : 's'}`;
}
